import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { api } from '@/shared/api/http'
import { onSessionExpired } from '@/shared/api/http'
import { tokenStore } from './tokenStore'
import type { LoginResponse, PermissionAction, UserProfile } from './types'

interface AuthState {
  user: UserProfile | null
  permissions: Record<string, { view: boolean; add: boolean; edit: boolean; delete: boolean }>
  /** True until the initial silent refresh settles, so guards do not bounce. */
  initialising: boolean
  login: (email: string, password: string, rememberMe: boolean) => Promise<void>
  logout: () => Promise<void>
  can: (permCode: string, action?: PermissionAction) => boolean
}

const AuthContext = createContext<AuthState | null>(null)

/**
 * Shared across callers while the call is in flight.
 *
 * React runs effects twice on mount in development, so without this one page
 * load sends two refreshes. Refresh tokens rotate on use, so the second one
 * presents a token the first has already consumed -- which is precisely the
 * signature of a stolen token, and the server is right to kill the session for
 * it. One page load must make one call.
 */
let restoreInFlight: Promise<LoginResponse> | null = null

function restoreSession(): Promise<LoginResponse> {
  if (!restoreInFlight) {
    const call = api.post<LoginResponse>('/auth/refresh')
    restoreInFlight = call
    // Cleared once settled rather than cached: a later mount must ask again,
    // or logging out and back in would replay a stale session.
    void call.finally(() => {
      restoreInFlight = null
    })
  }
  return restoreInFlight
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null)
  const [permissions, setPermissions] = useState<AuthState['permissions']>({})
  const [initialising, setInitialising] = useState(true)

  const applySession = useCallback((data: LoginResponse) => {
    tokenStore.setAccessToken(data.accessToken)
    setUser(data.profile)
    setPermissions(data.permissions ?? {})
  }, [])

  const clearSession = useCallback(() => {
    tokenStore.clear()
    setUser(null)
    setPermissions({})
  }, [])

  // On first load the access token is gone (it only ever lived in memory), but
  // the HttpOnly refresh cookie may still be valid. Try once; a failure here is
  // the normal "not logged in" case, not an error worth showing.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const data = await restoreSession()
        if (!cancelled) applySession(data)
      } catch {
        if (!cancelled) clearSession()
      } finally {
        if (!cancelled) setInitialising(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [applySession, clearSession])

  // The HTTP layer tells us when a refresh has definitively failed mid-session.
  useEffect(() => onSessionExpired(clearSession), [clearSession])

  const login = useCallback(
    // rememberMe is the server's decision to act on, not the client's: it
    // controls how long the refresh token lives and whether its cookie
    // survives the browser closing. Nothing about staying signed in is
    // decided here, because nothing here can be trusted to.
    async (email: string, password: string, rememberMe: boolean) => {
      const data = await api.post<LoginResponse>('/auth/login', { email, password, rememberMe })
      applySession(data)
    },
    [applySession],
  )

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout')
    } finally {
      // Clear locally even if the call failed: the user asked to leave, and a
      // network error must not strand them in a half-logged-in state.
      clearSession()
    }
  }, [clearSession])

  const can = useCallback(
    (permCode: string, action: PermissionAction = 'view') => {
      if (permissions.BILLING_ADMIN?.view) return true
      return permissions[permCode]?.[action] ?? false
    },
    [permissions],
  )

  const value = useMemo<AuthState>(
    () => ({ user, permissions, initialising, login, logout, can }),
    [user, permissions, initialising, login, logout, can],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth harus dipakai di dalam AuthProvider')
  return ctx
}
